/**
 * Shared Refinement Tile System
 * Used by both Onboarding Step 6 and Refine My Profile.
 *
 * Each tile has:
 *   id       – unique, used for answered-tile tracking
 *   name     – display title
 *   img      – emoji icon
 *   cat      – category key (matches REFINEMENT_SECTIONS)
 *   sub      – subtitle shown on card
 *   targets  – CONFIDENCE_ATTRS keys this card addresses
 *   gain     – confidence gain per answered swipe (0.15–0.40)
 *   depth    – 1=basic (onboarding+refine), 2=intermediate, 3=deep (refine only)
 *   effects  – { right: {…}, left: {…} } attribute values per swipe direction
 *   safe     – (opt) allergen keys that exclude this card
 *   notIfVegan / notIfVeg – (opt) dietary exclusion
 *   onlyIfDrink – (opt) show only for these drink choices
 *   onlyIfBevCat – (opt) show only if profile.beverage.preferred_bev_category includes one of these
 */

// ═══════════════════════════════════════════════════════════════
// TILE POOL — 96 tiles across 8 categories, 3 depth tiers
// ═══════════════════════════════════════════════════════════════

export const REFINEMENT_POOL = [

  // ────────────────────────────────────────────────────────────
  // TEXTURE (8 tiles: 5 depth-1, 2 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "tx-crispy",    name: "Crispy & Crunchy",       img: "🥨", cat: "texture",    sub: "Tempura, fried calamari, crackling",          depth: 1, targets: ["preferred_textures"], gain: 0.35,
    effects: { right: { preferred_textures: ["Crispy"] }, left: { avoided_textures: ["Crispy"] } } },
  { id: "tx-silky",     name: "Silky Smooth",           img: "🍮", cat: "texture",    sub: "Panna cotta, velouté, burrata",               depth: 1, targets: ["preferred_textures", "richness_pref"], gain: 0.30,
    effects: { right: { preferred_textures: ["Silky", "Smooth"], richness_pref: 4 }, left: { avoided_textures: ["Silky"] } } },
  { id: "tx-raw",       name: "Raw & Delicate",         img: "🐟", cat: "texture",    sub: "Sashimi, tartare, ceviche",                   depth: 1, targets: ["accepts_raw", "preferred_textures"], gain: 0.40, safe: ["fish", "shellfish"],
    effects: { right: { accepts_raw: true, preferred_textures: ["Raw"] }, left: { accepts_raw: false } } },
  { id: "tx-charred",   name: "Charred & Smoky",        img: "🔥", cat: "texture",    sub: "Wood-fired, charcoal grill, yakitori",        depth: 1, targets: ["preferred_textures", "preferred_cooking"], gain: 0.30,
    effects: { right: { preferred_textures: ["Charred"], preferred_cooking: ["Grilled", "Charcoal"] }, left: {} } },
  { id: "tx-mushy",     name: "Avoid Mushy Textures",   img: "🫠", cat: "texture",    sub: "Overcooked veg, stodgy bread, wet noodles",   depth: 1, targets: ["preferred_textures"], gain: 0.28,
    effects: { right: { avoided_textures: ["Mushy", "Soggy"] }, left: {} } },
  { id: "tx-chewy",     name: "Chewy & Dense",          img: "🥯", cat: "texture",    sub: "Mochi, bagels, al dente pasta, octopus",      depth: 2, targets: ["preferred_textures"], gain: 0.25,
    effects: { right: { preferred_textures: ["Chewy", "Dense"] }, left: { avoided_textures: ["Chewy"] } } },
  { id: "tx-flaky",     name: "Flaky & Layered",        img: "🥐", cat: "texture",    sub: "Croissant, filo, puff pastry, mille-feuille", depth: 2, targets: ["preferred_textures"], gain: 0.22,
    effects: { right: { preferred_textures: ["Flaky", "Layered"] }, left: {} } },
  { id: "tx-crunch-contrast", name: "Crunch Contrast",  img: "🌮", cat: "texture",    sub: "Crispy topping on soft base, textural contrast", depth: 3, targets: ["preferred_textures"], gain: 0.20,
    effects: { right: { preferred_textures: ["Contrast", "Crispy"] }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // TASTE (10 tiles: 5 depth-1, 3 depth-2, 2 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "ta-sweet",     name: "Sweet Finish",           img: "🍯", cat: "taste",      sub: "Caramel, mango lassi, dessert wines",         depth: 1, targets: ["sweetness_pref"], gain: 0.40,
    effects: { right: { sweetness_pref: 4 }, left: { sweetness_pref: 1 } } },
  { id: "ta-sour",      name: "Sour & Tangy",           img: "🍋", cat: "taste",      sub: "Pickles, tamarind, yuzu, citrus sauces",      depth: 1, targets: ["acidity_pref"], gain: 0.40,
    effects: { right: { acidity_pref: 4 }, left: { acidity_pref: 1 } } },
  { id: "ta-bitter",    name: "Bitter Notes",           img: "☕", cat: "taste",      sub: "Radicchio, dark chocolate, espresso finish",  depth: 1, targets: ["bitterness_pref"], gain: 0.40,
    effects: { right: { bitterness_pref: 4 }, left: { bitterness_pref: 1 } } },
  { id: "ta-umami",     name: "Deep Umami",             img: "🍖", cat: "taste",      sub: "Bone broth, aged parmesan, miso glaze",       depth: 1, targets: ["umami_pref"], gain: 0.28,
    effects: { right: { umami_pref: 5 }, left: { umami_pref: 2 } } },
  { id: "ta-rich",      name: "Rich & Fatty",           img: "🧈", cat: "taste",      sub: "Duck confit, butter sauces, heavy marbling",  depth: 1, targets: ["richness_pref", "indulgence_default"], gain: 0.28,
    effects: { right: { richness_pref: 4, indulgence_default: 4 }, left: { richness_pref: 2, indulgence_default: 2 } } },
  { id: "ta-herbal",    name: "Herbaceous & Fresh",     img: "🌿", cat: "taste",      sub: "Basil, cilantro, dill, mint — fresh green notes", depth: 2, targets: ["acidity_pref", "sweetness_pref"], gain: 0.22,
    effects: { right: { acidity_pref: 3 }, left: {} } },
  { id: "ta-smoky",     name: "Smoky Flavours",         img: "💨", cat: "taste",      sub: "Smoked paprika, chipotle, lapsang souchong", depth: 2, targets: ["umami_pref", "bitterness_pref"], gain: 0.22,
    effects: { right: { umami_pref: 4, bitterness_pref: 3 }, left: {} } },
  { id: "ta-floral",    name: "Floral & Fragrant",      img: "🌸", cat: "taste",      sub: "Rose water, elderflower, lavender, jasmine",  depth: 2, targets: ["sweetness_pref"], gain: 0.20,
    effects: { right: { sweetness_pref: 3 }, left: {} } },
  { id: "ta-funky",     name: "Funky & Fermented",      img: "🫙", cat: "taste",      sub: "Blue cheese, fish sauce, nattō, kimchi",      depth: 3, targets: ["umami_pref", "adventurousness"], gain: 0.20,
    effects: { right: { umami_pref: 5, novelty_pref: 4 }, left: { umami_pref: 2 } } },
  { id: "ta-savory-sweet", name: "Savoury over Sweet",  img: "🧂", cat: "taste",      sub: "Always choose savoury — skip dessert",        depth: 3, targets: ["sweetness_pref", "umami_pref"], gain: 0.18,
    effects: { right: { sweetness_pref: 1, umami_pref: 4 }, left: { sweetness_pref: 4 } } },

  // ────────────────────────────────────────────────────────────
  // COOKING (8 tiles: 4 depth-1, 3 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "ck-grill",     name: "Grilled & Seared",       img: "🥩", cat: "cooking",    sub: "Crosshatch marks, caramelised crust",         depth: 1, targets: ["preferred_cooking"], gain: 0.35,
    effects: { right: { preferred_cooking: ["Grilled"] }, left: {} } },
  { id: "ck-braise",    name: "Slow-braised & Tender",  img: "🍲", cat: "cooking",    sub: "Short rib, osso buco, pulled lamb",           depth: 1, targets: ["preferred_cooking", "preferred_textures"], gain: 0.30,
    effects: { right: { preferred_cooking: ["Braised"], preferred_textures: ["Tender"] }, left: {} } },
  { id: "ck-steam",     name: "Steamed & Clean",        img: "🫕", cat: "cooking",    sub: "Dim sum, fish en papillote, dumplings",       depth: 1, targets: ["preferred_cooking", "health_conscious"], gain: 0.25,
    effects: { right: { preferred_cooking: ["Steamed"], health_default: 4 }, left: {} } },
  { id: "ck-ferment",   name: "Fermented & Aged",       img: "🧀", cat: "cooking",    sub: "Kimchi, aged cheese, cured meats, nattō",     depth: 1, targets: ["preferred_cooking", "umami_pref", "adventurousness"], gain: 0.25,
    effects: { right: { preferred_cooking: ["Fermented"], novelty_pref: 4 }, left: {} } },
  { id: "ck-wok",       name: "Wok-fired & Stir-fry",   img: "🍳", cat: "cooking",    sub: "High heat, quick toss, smoky wok hei",        depth: 2, targets: ["preferred_cooking"], gain: 0.25,
    effects: { right: { preferred_cooking: ["Wok-fried", "Stir-fried"] }, left: {} } },
  { id: "ck-smoke",     name: "Smoked & Cured",         img: "🫔", cat: "cooking",    sub: "Cold-smoked salmon, pastrami, brisket",       depth: 2, targets: ["preferred_cooking", "umami_pref"], gain: 0.22,
    effects: { right: { preferred_cooking: ["Smoked", "Cured"], umami_pref: 4 }, left: {} } },
  { id: "ck-fry",       name: "Deep-fried Goodness",    img: "🍟", cat: "cooking",    sub: "Tempura, churros, fritto misto, karaage",     depth: 2, targets: ["preferred_cooking", "indulgence_default"], gain: 0.22,
    effects: { right: { preferred_cooking: ["Fried"], indulgence_default: 4 }, left: {} } },
  { id: "ck-raw-prep",  name: "Raw Preparations",       img: "🥒", cat: "cooking",    sub: "Carpaccio, poke, crudo — nothing cooked",     depth: 3, targets: ["preferred_cooking", "accepts_raw"], gain: 0.20,
    effects: { right: { preferred_cooking: ["Raw"], accepts_raw: true }, left: { accepts_raw: false } } },

  // ────────────────────────────────────────────────────────────
  // INDULGENCE (6 tiles: 3 depth-1, 2 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "in-indulgent", name: "Indulgent by Default",   img: "🍫", cat: "indulgence", sub: "Rich sauces, generous portions, butter",      depth: 1, targets: ["indulgence_default", "health_conscious"], gain: 0.40,
    effects: { right: { indulgence_default: 4, health_default: 2 }, left: { indulgence_default: 2, health_default: 4 } } },
  { id: "in-light",     name: "Light & Clean",          img: "🥗", cat: "indulgence", sub: "Grilled fish, grain bowls, bright salads",    depth: 1, targets: ["health_conscious", "portion_pref"], gain: 0.35,
    effects: { right: { health_default: 4, portion_pref: "light" }, left: { health_default: 2 } } },
  { id: "in-sharing",   name: "Sharing Plates",         img: "🫕", cat: "indulgence", sub: "Tapas, mezze, family-style tasting menus",    depth: 1, targets: ["portion_pref", "indulgence_default"], gain: 0.30,
    effects: { right: { portion_pref: "sharing" }, left: { portion_pref: "standard" } } },
  { id: "in-dessert",   name: "Always Dessert",         img: "🍰", cat: "indulgence", sub: "Never skip the sweet course",                 depth: 2, targets: ["sweetness_pref", "indulgence_default"], gain: 0.22,
    effects: { right: { sweetness_pref: 4, indulgence_default: 4 }, left: { sweetness_pref: 2 } } },
  { id: "in-bread",     name: "Bread & Carbs",          img: "🍞", cat: "indulgence", sub: "Fresh bread basket, pasta, rice, potatoes",   depth: 2, targets: ["indulgence_default"], gain: 0.20,
    effects: { right: { indulgence_default: 4 }, left: { health_default: 3 } } },
  { id: "in-tasting",   name: "Tasting Menu Fan",       img: "🍽️", cat: "indulgence", sub: "Multi-course, chef-curated progression",      depth: 3, targets: ["portion_pref", "adventurousness"], gain: 0.18,
    effects: { right: { portion_pref: "tasting", novelty_pref: 4 }, left: { portion_pref: "standard" } } },

  // ────────────────────────────────────────────────────────────
  // NOVELTY (6 tiles: 3 depth-1, 2 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "nv-unfamiliar", name: "Love the Unfamiliar",   img: "🌍", cat: "novelty",    sub: "Strange cuts, exotic fruits, new cuisines",   depth: 1, targets: ["novelty_pref", "adventurousness"], gain: 0.40,
    effects: { right: { novelty_pref: 5 }, left: { novelty_pref: 1 } } },
  { id: "nv-comfort",   name: "Comfort Food Always",    img: "🛋️", cat: "novelty",    sub: "Familiar flavours, reliable favourites",      depth: 1, targets: ["novelty_pref"], gain: 0.35,
    effects: { right: { novelty_pref: 1 }, left: { novelty_pref: 3 } } },
  { id: "nv-offal",     name: "Insects & Offal",        img: "🦗", cat: "novelty",    sub: "Bone marrow, sweetbreads, unusual cuts",      depth: 1, targets: ["adventurousness", "novelty_pref"], gain: 0.30, notIfVegan: true, notIfVeg: true,
    effects: { right: { novelty_pref: 5 }, left: { novelty_pref: 2 } } },
  { id: "nv-seasonal",  name: "Seasonal Eating",        img: "🍂", cat: "novelty",    sub: "Eat what's in season, follow the calendar",   depth: 2, targets: ["adventurousness"], gain: 0.20,
    effects: { right: { novelty_pref: 3, health_default: 3 }, left: {} } },
  { id: "nv-chefs",     name: "Chef's Choice",          img: "👨‍🍳", cat: "novelty",    sub: "Omakase, menu du chef, trust the kitchen",    depth: 2, targets: ["adventurousness", "novelty_pref"], gain: 0.25,
    effects: { right: { novelty_pref: 5 }, left: { novelty_pref: 2 } } },
  { id: "nv-molecular", name: "Molecular & Modern",     img: "🧪", cat: "novelty",    sub: "Foams, spheres, liquid nitrogen, deconstruction", depth: 3, targets: ["adventurousness", "novelty_pref"], gain: 0.18,
    effects: { right: { novelty_pref: 5 }, left: { novelty_pref: 1 } } },

  // ────────────────────────────────────────────────────────────
  // CUISINE (12 tiles: 4 depth-1, 5 depth-2, 3 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "cs-mideast",   name: "Middle Eastern Flavours", img: "🧆", cat: "cuisine",    sub: "Za'atar, harissa, pomegranate molasses",     depth: 1, targets: ["loves_asian"], gain: 0.20,
    effects: { right: { cuisine_boost: { "Middle Eastern": 3 } }, left: {} } },
  { id: "cs-latin",     name: "Latin & Mexican",        img: "🌮", cat: "cuisine",    sub: "Tacos al pastor, ceviche, mole",              depth: 1, targets: ["loves_american"], gain: 0.20,
    effects: { right: { cuisine_boost: { "Mexican": 3, "Latin American": 2 } }, left: {} } },
  { id: "cs-japanese",  name: "Japanese Precision",     img: "🍱", cat: "cuisine",    sub: "Ramen, kaiseki, yakitori, soba",              depth: 1, targets: ["loves_asian"], gain: 0.20,
    effects: { right: { cuisine_boost: { "Japanese": 3 } }, left: {} } },
  { id: "cs-french",    name: "French Classics",        img: "🥖", cat: "cuisine",    sub: "Bistro, brasserie, butter-forward",           depth: 1, targets: ["loves_european"], gain: 0.20,
    effects: { right: { cuisine_boost: { "French": 3 } }, left: {} } },
  { id: "cs-korean",    name: "Korean Bold",            img: "🥘", cat: "cuisine",    sub: "Bibimbap, Korean BBQ, kimchi jjigae",         depth: 2, targets: ["loves_asian"], gain: 0.18,
    effects: { right: { cuisine_boost: { "Korean": 3 } }, left: {} } },
  { id: "cs-thai",      name: "Thai Balance",           img: "🍜", cat: "cuisine",    sub: "Green curry, pad thai, som tam, tom yum",     depth: 2, targets: ["loves_asian"], gain: 0.18,
    effects: { right: { cuisine_boost: { "Thai": 3 } }, left: {} } },
  { id: "cs-italian",   name: "Italian Soul",           img: "🍝", cat: "cuisine",    sub: "Fresh pasta, risotto, antipasti, regional",    depth: 2, targets: ["loves_european"], gain: 0.18,
    effects: { right: { cuisine_boost: { "Italian": 3 } }, left: {} } },
  { id: "cs-indian",    name: "Indian Depth",           img: "🫓", cat: "cuisine",    sub: "Regional curries, tandoori, dosa, thali",     depth: 2, targets: ["loves_indian"], gain: 0.18,
    effects: { right: { cuisine_boost: { "Indian": 3 } }, left: {} } },
  { id: "cs-spanish",   name: "Spanish & Portuguese",   img: "🦐", cat: "cuisine",    sub: "Tapas, pintxos, bacalhau, cataplana",         depth: 2, targets: ["loves_european"], gain: 0.18,
    effects: { right: { cuisine_boost: { "Spanish": 3, "Portuguese": 2 } }, left: {} } },
  { id: "cs-chinese",   name: "Chinese Regions",        img: "🥟", cat: "cuisine",    sub: "Sichuan, Cantonese, Hunanese, dim sum",       depth: 3, targets: ["loves_asian"], gain: 0.15,
    effects: { right: { cuisine_boost: { "Chinese": 3 } }, left: {} } },
  { id: "cs-nordic",    name: "Nordic & Modern",        img: "🫐", cat: "cuisine",    sub: "New Nordic, seasonal, minimal, pure flavours", depth: 3, targets: ["loves_european"], gain: 0.15,
    effects: { right: { cuisine_boost: { "Nordic": 3 } }, left: {} } },
  { id: "cs-african",   name: "African Cuisines",       img: "🍛", cat: "cuisine",    sub: "Ethiopian injera, West African jollof, tagine", depth: 3, targets: ["loves_asian"], gain: 0.15,
    effects: { right: { cuisine_boost: { "African": 3 } }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // PROTEIN (9 tiles: 5 depth-1, 3 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "pr-beef",      name: "Love Beef & Steak",      img: "🥩", cat: "protein",    sub: "Ribeye, tenderloin, wagyu, short rib",        depth: 1, targets: ["likes_beef"], gain: 0.35, notIfVegan: true, notIfVeg: true,
    effects: { right: { protein_boost: ["Beef"] }, left: { avoided_proteins: ["Beef"] } } },
  { id: "pr-chicken",   name: "Chicken Always Works",   img: "🍗", cat: "protein",    sub: "Roasted, grilled, braised, fried",            depth: 1, targets: ["likes_chicken"], gain: 0.30,
    effects: { right: { protein_boost: ["Poultry"] }, left: {} } },
  { id: "pr-plant",     name: "Plant-based Meals",      img: "🥬", cat: "protein",    sub: "Tofu, mushroom, lentil-forward dishes",       depth: 1, targets: ["likes_plant", "health_conscious"], gain: 0.25,
    effects: { right: { protein_boost: ["Plant"], health_default: 4 }, left: {} } },
  { id: "pr-lamb",      name: "Love Lamb",              img: "🐑", cat: "protein",    sub: "Rack, shoulder, tagine, chops",               depth: 1, targets: ["likes_lamb"], gain: 0.35,
    effects: { right: { protein_boost: ["Lamb"] }, left: { avoided_proteins: ["Lamb"] } } },
  { id: "pr-shellfish", name: "Raw Shellfish",          img: "🦪", cat: "protein",    sub: "Oysters on ice, clams, half-shell scallops",  depth: 1, targets: ["likes_seafood", "accepts_raw"], gain: 0.30, safe: ["shellfish"],
    effects: { right: { accepts_raw: true, protein_boost: ["Seafood"] }, left: { avoided_proteins: ["Shellfish"] } } },
  { id: "pr-oily-fish", name: "Oily Fish",              img: "🐟", cat: "protein",    sub: "Salmon, mackerel, sardines, tuna belly",      depth: 2, targets: ["likes_seafood"], gain: 0.22, safe: ["fish"],
    effects: { right: { protein_boost: ["Fish"] }, left: {} } },
  { id: "pr-game",      name: "Game Meats",             img: "🦌", cat: "protein",    sub: "Venison, wild boar, duck, pheasant",          depth: 2, targets: ["likes_beef", "adventurousness"], gain: 0.22, notIfVegan: true, notIfVeg: true,
    effects: { right: { protein_boost: ["Game"], novelty_pref: 4 }, left: {} } },
  { id: "pr-cured",     name: "Cured & Charcuterie",    img: "🥓", cat: "protein",    sub: "Prosciutto, bresaola, salami, coppa",         depth: 2, targets: ["likes_beef", "umami_pref"], gain: 0.20, notIfVegan: true, notIfVeg: true,
    effects: { right: { protein_boost: ["Cured Meats"], preferred_cooking: ["Cured"] }, left: {} } },
  { id: "pr-egg",       name: "Egg-forward Dishes",     img: "🥚", cat: "protein",    sub: "Shakshuka, omelette, ramen egg, soufflé",     depth: 3, targets: ["likes_plant"], gain: 0.18,
    effects: { right: { protein_boost: ["Egg"] }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // BEVERAGE — WINE (12 tiles: 2 depth-1, 6 depth-2, 4 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "bv-wine-bold-red", name: "Bold Red Wines",     img: "🍷", cat: "beverage",   sub: "Cabernet, Malbec, Shiraz — full & tannic",    depth: 1, targets: ["wine_style_pref"], gain: 0.30, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["Red"], wine_body_pref: 5, wine_tannin_pref: 4 }, left: {} } },
  { id: "bv-wine-crisp-white", name: "Crisp White Wines", img: "🥂", cat: "beverage", sub: "Sauvignon Blanc, Pinot Grigio, Albariño",     depth: 1, targets: ["wine_style_pref"], gain: 0.30, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["White"], wine_acidity_pref: 4, wine_body_pref: 1 }, left: {} } },
  { id: "bv-wine-rose",  name: "Dry Rosé",              img: "🌸", cat: "beverage",   sub: "Provence-style, crisp, pale pink, summer",    depth: 2, targets: ["wine_style_pref"], gain: 0.25, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["Rosé"], wine_sweetness_pref: 1 }, left: {} } },
  { id: "bv-wine-sparkling", name: "Sparkling & Champagne", img: "🫧", cat: "beverage", sub: "Champagne, Prosecco, Cava, Crémant",       depth: 2, targets: ["wine_style_pref"], gain: 0.25, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["Sparkling"], wine_acidity_pref: 3 }, left: {} } },
  { id: "bv-wine-sweet", name: "Sweet & Dessert Wines", img: "🍯", cat: "beverage",   sub: "Sauternes, Tokaji, Port, late harvest",       depth: 2, targets: ["wine_style_pref"], gain: 0.25, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_sweetness_pref: 5, wine_body_pref: 4 }, left: { wine_sweetness_pref: 1 } } },
  { id: "bv-wine-light-red", name: "Light & Elegant Reds", img: "🌹", cat: "beverage", sub: "Pinot Noir, Gamay, Nebbiolo — silky & subtle", depth: 2, targets: ["wine_style_pref"], gain: 0.22, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["Red"], wine_body_pref: 2, wine_tannin_pref: 2 }, left: {} } },
  { id: "bv-wine-oaky", name: "Oaky & Rich Whites",     img: "🪵", cat: "beverage",   sub: "Oaked Chardonnay, Viognier, barrel-aged",     depth: 2, targets: ["wine_style_pref"], gain: 0.22, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["White"], wine_oak_pref: 4, wine_body_pref: 3 }, left: { wine_oak_pref: 1 } } },
  { id: "bv-wine-mineral", name: "Mineral & Crisp",     img: "💎", cat: "beverage",   sub: "Chablis, dry Riesling, volcanic, chalky",     depth: 2, targets: ["wine_style_pref"], gain: 0.22, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_oak_pref: 1, wine_acidity_pref: 4 }, left: {} } },
  { id: "bv-wine-tannin", name: "High Tannin Wines",    img: "🫠", cat: "beverage",   sub: "Barolo, Tannat, young Cab — grippy & bold",   depth: 3, targets: ["wine_style_pref"], gain: 0.18, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_tannin_pref: 5 }, left: { wine_tannin_pref: 1 } } },
  { id: "bv-wine-natural", name: "Natural & Orange Wine", img: "🧡", cat: "beverage",  sub: "Skin-contact, unfiltered, lo-fi winemaking",  depth: 3, targets: ["wine_style_pref", "adventurousness"], gain: 0.18, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_style_pref: ["Orange", "Natural"], novelty_pref: 4 }, left: {} } },
  { id: "bv-wine-oldworld", name: "Old World Terroir",  img: "🏰", cat: "beverage",   sub: "Burgundy, Barolo, Rioja — place over fruit",  depth: 3, targets: ["wine_style_pref"], gain: 0.15, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_region_pref: ["France", "Italy", "Spain"] }, left: {} } },
  { id: "bv-wine-newworld", name: "New World Fruit-forward", img: "🌞", cat: "beverage", sub: "Napa, Barossa, Mendoza — ripe & bold",      depth: 3, targets: ["wine_style_pref"], gain: 0.15, onlyIfDrink: ["Wine", "Mood"],
    effects: { right: { wine_region_pref: ["USA", "Australia", "Argentina", "Chile", "South Africa"] }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // BEVERAGE — COCKTAILS (8 tiles: 2 depth-1, 4 depth-2, 2 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "bv-ck-classic", name: "Classic Cocktails",      img: "🍸", cat: "beverage",   sub: "Negroni, Manhattan, Martini, Old Fashioned",  depth: 1, targets: ["cocktail_style_pref"], gain: 0.35, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { cocktail_style_pref: ["Classic"] }, left: {} } },
  { id: "bv-ck-bitter", name: "Bitter & Aperitif",      img: "🥃", cat: "beverage",   sub: "Aperol Spritz, Campari Soda, Americano",      depth: 1, targets: ["cocktail_style_pref"], gain: 0.30, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { cocktail_style_pref: ["Bitter", "Aperitif"] }, left: {} } },
  { id: "bv-ck-tropical", name: "Tropical & Tiki",      img: "🌴", cat: "beverage",   sub: "Mai Tai, Piña Colada, Daiquiri, rum punch",   depth: 2, targets: ["cocktail_style_pref"], gain: 0.25, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { cocktail_style_pref: ["Tropical", "Tiki"], spirit_pref: ["Rum"] }, left: {} } },
  { id: "bv-ck-whiskey", name: "Whiskey Cocktails",     img: "🥃", cat: "beverage",   sub: "Old Fashioned, Whiskey Sour, Penicillin",     depth: 2, targets: ["cocktail_style_pref"], gain: 0.25, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { spirit_pref: ["Whiskey", "Bourbon"] }, left: {} } },
  { id: "bv-ck-gin",    name: "Gin & Botanicals",       img: "🫒", cat: "beverage",   sub: "G&T, Gimlet, Aviation, botanical-forward",    depth: 2, targets: ["cocktail_style_pref"], gain: 0.25, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { spirit_pref: ["Gin"], cocktail_style_pref: ["Botanical"] }, left: {} } },
  { id: "bv-ck-tequila", name: "Tequila & Mezcal",     img: "🌵", cat: "beverage",   sub: "Margarita, Paloma, smoky mezcal cocktails",   depth: 2, targets: ["cocktail_style_pref"], gain: 0.25, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { spirit_pref: ["Tequila", "Mezcal"] }, left: {} } },
  { id: "bv-ck-lowabv", name: "Low-ABV & Spritzes",     img: "🧊", cat: "beverage",   sub: "Hugo, Spritz, Sbagliato — light & refreshing", depth: 3, targets: ["cocktail_style_pref"], gain: 0.18, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { cocktail_style_pref: ["Low-ABV", "Spritz"] }, left: {} } },
  { id: "bv-ck-creative", name: "Creative Cocktails",   img: "🧪", cat: "beverage",   sub: "Chef cocktails, molecular, unexpected pairings", depth: 3, targets: ["cocktail_style_pref", "adventurousness"], gain: 0.18, onlyIfDrink: ["Cocktails", "Mood"],
    effects: { right: { cocktail_style_pref: ["Creative", "Modern"], novelty_pref: 4 }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // BEVERAGE — BEER (4 tiles: 1 depth-1, 2 depth-2, 1 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "bv-beer-craft", name: "Craft Beers & Ales",    img: "🍺", cat: "beverage",   sub: "IPAs, stouts, session lagers, Belgian ales",  depth: 1, targets: ["na_bev_pref"], gain: 0.30, onlyIfDrink: ["Beer", "Mood"],
    effects: { right: { na_bev_pref: ["Craft Beer"] }, left: {} } },
  { id: "bv-beer-dark",  name: "Dark & Stout",          img: "🫗", cat: "beverage",   sub: "Stout, porter, dark lager, imperial",         depth: 2, targets: ["na_bev_pref"], gain: 0.22, onlyIfDrink: ["Beer", "Mood"],
    effects: { right: { na_bev_pref: ["Stout", "Porter"] }, left: {} } },
  { id: "bv-beer-light", name: "Light & Session",       img: "🍻", cat: "beverage",   sub: "Pilsner, wheat beer, session IPA, kolsch",    depth: 2, targets: ["na_bev_pref"], gain: 0.22, onlyIfDrink: ["Beer", "Mood"],
    effects: { right: { na_bev_pref: ["Pilsner", "Wheat Beer"] }, left: {} } },
  { id: "bv-beer-sour",  name: "Sour & Wild Beers",     img: "🍋", cat: "beverage",   sub: "Lambic, gose, Berliner Weisse, fruited sours", depth: 3, targets: ["na_bev_pref", "adventurousness"], gain: 0.18, onlyIfDrink: ["Beer", "Mood"],
    effects: { right: { na_bev_pref: ["Sour Beer"], novelty_pref: 4 }, left: {} } },

  // ────────────────────────────────────────────────────────────
  // BEVERAGE — NON-ALCOHOLIC (7 tiles: 1 depth-1, 4 depth-2, 2 depth-3)
  // ────────────────────────────────────────────────────────────
  { id: "bv-na-premium", name: "Premium No-Alcohol",    img: "🧋", cat: "beverage",   sub: "Seedlip, NA wines, botanical sodas",          depth: 1, targets: ["na_bev_pref"], gain: 0.35, onlyIfDrink: ["Non-alcoholic", "Water", "Mood"],
    effects: { right: { na_bev_pref: ["Premium NA", "Botanical"] }, left: {} } },
  { id: "bv-na-espresso", name: "Espresso Lover",       img: "☕", cat: "beverage",   sub: "Espresso, ristretto, macchiato — intense",    depth: 2, targets: ["na_bev_pref"], gain: 0.22,
    effects: { right: { na_bev_pref: ["Espresso", "Black Coffee"] }, left: {} } },
  { id: "bv-na-milkcoffee", name: "Milk Coffee",        img: "🥛", cat: "beverage",   sub: "Latte, cappuccino, flat white, cortado",      depth: 2, targets: ["na_bev_pref"], gain: 0.22,
    effects: { right: { na_bev_pref: ["Latte", "Cappuccino"] }, left: {} } },
  { id: "bv-na-tea",    name: "Specialty Tea",           img: "🍵", cat: "beverage",   sub: "Matcha, pu-erh, oolong, ceremonial, chai",    depth: 2, targets: ["na_bev_pref"], gain: 0.22,
    effects: { right: { na_bev_pref: ["Tea", "Matcha"] }, left: {} } },
  { id: "bv-na-juice",  name: "Fresh Juices & Smoothies", img: "🧃", cat: "beverage", sub: "Cold-pressed, smoothie bowls, açaí",           depth: 2, targets: ["na_bev_pref", "health_conscious"], gain: 0.20,
    effects: { right: { na_bev_pref: ["Juice", "Smoothie"], health_default: 3 }, left: {} } },
  { id: "bv-na-kombucha", name: "Kombucha & Fermented", img: "🫙", cat: "beverage",   sub: "Kombucha, kefir, shrubs, tepache",            depth: 3, targets: ["na_bev_pref", "adventurousness"], gain: 0.18,
    effects: { right: { na_bev_pref: ["Kombucha", "Fermented"], novelty_pref: 3 }, left: {} } },
  { id: "bv-na-tonic",  name: "Tonic & Sparkling",      img: "🫧", cat: "beverage",   sub: "Premium tonics, flavoured sparkling water",   depth: 3, targets: ["na_bev_pref"], gain: 0.15,
    effects: { right: { na_bev_pref: ["Tonic", "Sparkling Water"] }, left: {} } },
];


// ═══════════════════════════════════════════════════════════════
// REFINEMENT SECTIONS
// ═══════════════════════════════════════════════════════════════

export const REFINEMENT_SECTIONS = [
  { key: "texture",    icon: "✨",  label: "Textures",        attrs: ["preferred_textures", "accepts_raw"], desc: "Crispy, smooth, raw, charred" },
  { key: "taste",      icon: "👅",  label: "Taste Profile",   attrs: ["richness_pref", "umami_pref", "sweetness_pref", "acidity_pref", "bitterness_pref"], desc: "Sweet, sour, bitter, umami" },
  { key: "cooking",    icon: "🔥",  label: "Cooking Styles",  attrs: ["preferred_cooking"], desc: "Grilled, braised, steamed, fermented" },
  { key: "indulgence", icon: "⚖️", label: "Indulgence",      attrs: ["indulgence_default", "health_conscious", "portion_pref"], desc: "Rich vs light, portion sizes" },
  { key: "novelty",    icon: "🧭",  label: "Adventurousness", attrs: ["adventurousness", "novelty_pref"], desc: "How bold are you?" },
  { key: "cuisine",    icon: "🥘",  label: "Cuisines",        attrs: ["loves_asian", "loves_european", "loves_american", "loves_indian"], desc: "Explore new cuisines" },
  { key: "protein",    icon: "🥩",  label: "Proteins",        attrs: ["likes_beef", "likes_seafood", "likes_chicken", "likes_lamb", "likes_plant"], desc: "Favorite proteins" },
  { key: "beverage",   icon: "🍷",  label: "Beverages",       attrs: ["wine_style_pref", "cocktail_style_pref", "na_bev_pref"], desc: "Wine, cocktails, NA drinks" },
];


// ═══════════════════════════════════════════════════════════════
// ANSWERED TILE TRACKING (localStorage)
// ═══════════════════════════════════════════════════════════════

const ANSWERED_KEY = "consoome_answered_tiles";
const RECENTLY_SHOWN_KEY = "consoome_recently_shown";
const MAX_RECENT_SESSIONS = 3;

export function loadAnsweredTileIds() {
  try { return new Set(JSON.parse(localStorage.getItem(ANSWERED_KEY) || "[]")); }
  catch { return new Set(); }
}

export function saveAnsweredTileIds(idSet) {
  localStorage.setItem(ANSWERED_KEY, JSON.stringify([...idSet]));
}

export function markTilesAnswered(tileIds) {
  const answered = loadAnsweredTileIds();
  tileIds.forEach(id => answered.add(id));
  saveAnsweredTileIds(answered);
  return answered;
}

/**
 * Recently-shown tracking — stores tile IDs seen in last N sessions.
 * Used to deprioritise (not hard-block) tiles for variety.
 */
export function loadRecentlyShown() {
  try { return JSON.parse(localStorage.getItem(RECENTLY_SHOWN_KEY) || "[]"); }
  catch { return []; }
}

export function recordShownTiles(tileIds) {
  const sessions = loadRecentlyShown();
  sessions.push([...tileIds]);
  // Keep only last MAX_RECENT_SESSIONS sessions
  while (sessions.length > MAX_RECENT_SESSIONS) sessions.shift();
  localStorage.setItem(RECENTLY_SHOWN_KEY, JSON.stringify(sessions));
}

function getRecentlyShownSet() {
  const sessions = loadRecentlyShown();
  const set = new Set();
  sessions.forEach(s => s.forEach(id => set.add(id)));
  return set;
}

/**
 * Fisher-Yates shuffle (in-place).
 */
function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Randomized sort: sort by score, but add small random jitter
 * so equally-scored tiles appear in different orders each time.
 * Also applies a soft penalty for recently-shown tiles.
 */
function scoredSortWithVariety(scored, recentSet) {
  const RECENT_PENALTY = 0.15; // soft deprioritise, not hard block
  const JITTER_RANGE = 0.05;   // small random noise for variety

  return scored.map(card => {
    let finalScore = card._score;
    // Penalise recently-shown tiles
    if (recentSet.has(card.id)) finalScore -= RECENT_PENALTY;
    // Add small random jitter for variety among equally-scored tiles
    finalScore += (Math.random() - 0.5) * JITTER_RANGE;
    return { ...card, _finalScore: finalScore };
  }).sort((a, b) => b._finalScore - a._finalScore);
}


// ═══════════════════════════════════════════════════════════════
// TILE SELECTION — REFINE MY PROFILE
// ═══════════════════════════════════════════════════════════════

/**
 * Select tiles for a refinement session within a specific section.
 * Progressive: avoids answered tiles, prefers lower depth first,
 * scores by gap coverage, limits per session.
 *
 * @param {string} sectionKey – REFINEMENT_SECTIONS key
 * @param {Object} profile – user profile (safety, beverage, _confidenceScores, etc.)
 * @param {Set} answeredIds – set of already-answered tile IDs
 * @param {number} maxCards – max tiles per session (default 8)
 * @returns {Array} selected tiles
 */
export function selectTilesForRefine(sectionKey, profile, answeredIds, maxCards = 8) {
  const section = REFINEMENT_SECTIONS.find(s => s.key === sectionKey);
  if (!section) return [];

  const scores = profile._confidenceScores || {};
  const drinkChoice = profile.beverage?.preferred_bev_category?.[0] || "";

  // 1. Filter pool by section + safety
  let pool = REFINEMENT_POOL.filter(card => {
    // Must match section by targets or category
    const matchesSection = card.targets.some(t => section.attrs.includes(t)) || card.cat === sectionKey;
    if (!matchesSection) return false;

    // Safety filters
    if (card.safe) {
      for (const allergen of card.safe) {
        if (profile.safety?.[`has_${allergen}`] || profile.safety?.[allergen]) return false;
      }
    }
    if (card.notIfVegan && profile.safety?.is_vegan) return false;
    if (card.notIfVeg && profile.safety?.is_vegetarian) return false;

    // Drink context — for beverage section, filter by user's drink preferences
    if (card.onlyIfDrink) {
      const bevCats = profile.beverage?.preferred_bev_category || [];
      const drinksAlcohol = profile.beverage?.drinks_alcohol !== false;
      // In refine mode, be more permissive — show if any bev cat matches OR if "Mood" is in onlyIfDrink
      const hasMatch = card.onlyIfDrink.some(d =>
        bevCats.includes(d) || d === "Mood" || (d === "Wine" && bevCats.some(b => b === "Wine")) ||
        (d === "Cocktails" && bevCats.some(b => b === "Cocktails")) ||
        (d === "Beer" && bevCats.some(b => b === "Beer"))
      );
      // For NA tiles, show if not drinking alcohol
      const isNATile = card.onlyIfDrink.includes("Non-alcoholic") || card.onlyIfDrink.includes("Water");
      if (!hasMatch && !(isNATile && !drinksAlcohol)) return false;
    }

    return true;
  });

  // 2. Exclude answered tiles
  pool = pool.filter(card => !answeredIds.has(card.id));

  // 3. If pool is empty after exclusion (all answered), allow re-showing depth 3 tiles
  if (pool.length === 0) {
    pool = REFINEMENT_POOL.filter(card => {
      const matchesSection = card.targets.some(t => section.attrs.includes(t)) || card.cat === sectionKey;
      if (!matchesSection) return false;
      if (card.safe) { for (const a of card.safe) { if (profile.safety?.[`has_${a}`] || profile.safety?.[a]) return false; } }
      if (card.notIfVegan && profile.safety?.is_vegan) return false;
      if (card.notIfVeg && profile.safety?.is_vegetarian) return false;
      return card.depth >= 3; // Only recycle deep tiles
    });
  }

  // 4. Score by: depth priority (lower depth first) + gap coverage
  const scored = pool.map(card => {
    let gapScore = 0;
    card.targets.forEach(t => {
      const conf = scores[t] || 0;
      const weight = 1 - conf; // Higher weight for lower confidence
      gapScore += weight * card.gain;
    });

    // Depth penalty: prefer showing depth 1 before 2 before 3
    const depthPenalty = (card.depth - 1) * 0.3;

    return { ...card, _score: gapScore - depthPenalty };
  });

  // 5. Randomised sort with recently-shown deprioritisation
  const recentSet = getRecentlyShownSet();
  const sorted = scoredSortWithVariety(scored, recentSet);

  // 6. Select up to maxCards
  const selected = sorted.slice(0, maxCards);

  // 7. Record what we're showing for future variety tracking
  recordShownTiles(selected.map(c => c.id));

  return selected;
}


// ═══════════════════════════════════════════════════════════════
// TILE SELECTION — ONBOARDING STEP 6
// ═══════════════════════════════════════════════════════════════

/**
 * Build an adaptive deck for onboarding Step 6.
 * Uses depth 1-2 tiles, gap-driven scoring, dimension guarantee.
 *
 * @param {Array} gaps – from identifyGaps()
 * @param {Object} formData – onboarding form data
 * @param {number} count – target deck size (default 8)
 * @returns {Array} selected tiles
 */
export function buildAdaptiveDeck(gaps, formData, count = 8) {
  const isVegan = formData.dietary?.is_vegan;
  const isVegetarian = formData.dietary?.is_vegetarian;
  const neverEat = formData.neverEat || [];
  const drinkChoice = formData.drinkChoice;

  // Filter: respect dietary identity, allergens, drink context, never-eat, depth ≤ 2
  const pool = REFINEMENT_POOL.filter(card => {
    if (card.depth > 2) return false; // Step 6 only uses depth 1-2
    if (card.notIfVegan && isVegan) return false;
    if (card.notIfVeg && isVegetarian) return false;
    if (card.safe) {
      for (const allergen of card.safe) {
        if (formData.allergens?.[allergen]) return false;
      }
    }
    if (card.onlyIfDrink && !card.onlyIfDrink.includes(drinkChoice)) return false;
    if (neverEat.some(ne => card.name.toLowerCase().includes(ne.toLowerCase()))) return false;
    return true;
  });

  // Score: how much gap coverage does each card provide?
  const scored = pool.map(card => {
    let score = 0;
    gaps.forEach(gap => {
      if (card.targets.includes(gap.key)) score += gap.weight * (1 - gap.value) * card.gain;
    });
    // Slight preference for depth 1 in onboarding
    if (card.depth === 1) score *= 1.15;
    return { ...card, _score: score };
  });

  // Apply randomised sort with recently-shown deprioritisation
  const recentSet = getRecentlyShownSet();
  const sorted = scoredSortWithVariety(scored, recentSet);

  // DIMENSION GUARANTEE: the 5 critical unresolved dimensions each get ≥1 card
  // For each dim, pick from top candidates with randomisation among similar scores
  const PRIORITY_DIMS = ["texture", "taste", "cooking", "indulgence", "novelty"];
  const selected = [];

  for (const dim of PRIORITY_DIMS) {
    // Get all candidates for this dim, then pick randomly from top 3
    const candidates = sorted.filter(c => c.cat === dim && !selected.includes(c));
    if (candidates.length > 0) {
      const topN = candidates.slice(0, Math.min(3, candidates.length));
      selected.push(topN[Math.floor(Math.random() * topN.length)]);
    }
  }

  // FILL: top remaining cards, max 2 per category for breadth
  const catCount = {};
  selected.forEach(c => { catCount[c.cat] = (catCount[c.cat] || 0) + 1; });

  for (const card of sorted) {
    if (selected.length >= count) break;
    if (selected.includes(card)) continue;
    const cc = catCount[card.cat] || 0;
    if (cc >= 2) continue;
    selected.push(card);
    catCount[card.cat] = cc + 1;
  }

  // Fallback: relax cap if still under minimum
  if (selected.length < 6) {
    for (const card of sorted) {
      if (selected.length >= count) break;
      if (!selected.includes(card)) selected.push(card);
    }
  }

  // Record what we're showing for future variety tracking
  recordShownTiles(selected.map(c => c.id));

  return selected;
}


// ═══════════════════════════════════════════════════════════════
// SECTION CONFIDENCE
// ═══════════════════════════════════════════════════════════════

export function getSectionConfidence(section, scores) {
  if (!scores) return 0;
  const vals = section.attrs.map(a => scores[a] || 0);
  return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 100) : 0;
}

/**
 * Get section depth info — how many tiles answered, how many remain.
 */
export function getSectionDepthInfo(sectionKey, answeredIds) {
  const section = REFINEMENT_SECTIONS.find(s => s.key === sectionKey);
  if (!section) return { answered: 0, total: 0, remaining: 0, maxDepthReached: 0 };

  const sectionTiles = REFINEMENT_POOL.filter(card =>
    card.targets.some(t => section.attrs.includes(t)) || card.cat === sectionKey
  );
  const answeredTiles = sectionTiles.filter(t => answeredIds.has(t.id));
  const maxDepth = answeredTiles.reduce((m, t) => Math.max(m, t.depth), 0);

  return {
    answered: answeredTiles.length,
    total: sectionTiles.length,
    remaining: sectionTiles.length - answeredTiles.length,
    maxDepthReached: maxDepth,
  };
}


// ═══════════════════════════════════════════════════════════════
// MERGE EFFECTS (shared utility)
// ═══════════════════════════════════════════════════════════════

export function mergeEffects(current, effects) {
  const next = { ...current };
  for (const [key, val] of Object.entries(effects)) {
    if (Array.isArray(val)) {
      next[key] = [...new Set([...(next[key] || []), ...val])];
    } else if ((key === "cuisine_boost" || key === "protein_boost") && typeof val === "object" && !Array.isArray(val)) {
      const existing = next[key] || {};
      for (const [c, s] of Object.entries(val)) {
        existing[c] = Math.max(existing[c] || 0, s);
      }
      next[key] = existing;
    } else {
      next[key] = val;
    }
  }
  return next;
}

// ─── Level 2 Sliders (Refinement — precise numeric preferences) ───────────
// Pilot: only TASTE and BEVERAGE (wine) sections support Level 2 sliders.
// Other sections continue using Level 1 swipe tiles.
export const LEVEL2_SLIDERS = {
  taste: [
    { key: "sweetness_pref", label: "Sweetness", min: 0, max: 5 },
    { key: "acidity_pref", label: "Acidity / Tanginess", min: 0, max: 5 },
    { key: "bitterness_pref", label: "Bitter Notes", min: 0, max: 5 },
    { key: "umami_pref", label: "Umami / Savory Depth", min: 0, max: 5 },
    { key: "richness_pref", label: "Richness / Fattiness", min: 0, max: 5 },
  ],
  beverage_wine: [
    { key: "wine_body_pref", label: "Body", min: 0, max: 5 },
    { key: "wine_tannin_pref", label: "Tannin", min: 0, max: 5 },
    { key: "wine_acidity_pref", label: "Acidity", min: 0, max: 5 },
    { key: "wine_sweetness_pref", label: "Sweetness", min: 0, max: 5 },
    { key: "wine_oak_pref", label: "Oak", min: 0, max: 5 },
  ],
};
